import { useState } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { Banner, Button, Card, Field, Input, Spinner } from '../components/ui';
import { useSession } from '../lib/session';
import { useAsync } from '../lib/useAsync';
import ParcelFile from './ParcelFile';
import { doorToken, isAdmitted, isParcelId, isSeatKind, parcelDoor, seatLabels, type SeatKind } from './model';
import { mutateParcel, parcelApi, type ParcelAccess, type ParcelAction } from './service';

/**
 * The route chooses a door, never an identity. A door opens one of two ways:
 * the seat capability token carried by the URL (Run 6 — no sign-in), or an
 * authenticated session whose admitted seat the backend confirms. Without
 * either there is a file wall, not a sign-in bounce: the URL is the seat.
 */
export default function ParcelDoor({ support = false, join = false }: { support?: boolean; join?: boolean }) {
  const { id, seat } = useParams();
  const [search] = useSearchParams();
  const { contractor } = useSession();
  if (!isParcelId(id) || !isSeatKind(seat)) return <Banner title="Parcel door not found">Use your assigned parcel URL.</Banner>;
  if (support) {
    // CS view-as stays a bearer surface; a seat token is never a support credential.
    if (!contractor) return null;
    return <LoadDoor key={`${contractor.id}/${id}/${seat}/cs`} id={id} seat={seat} support token={null} />;
  }
  const token = doorToken(id, seat, search.get('t'));
  if (token) {
    // Keying prevents old parcel data, mutation state, and in-flight results crossing URLs.
    return join ? <ParcelAdmission key={`t/${id}/${seat}`} id={id} seat={seat} token={token} />
      : <LoadDoor key={`t/${id}/${seat}`} id={id} seat={seat} support={false} token={token} />;
  }
  if (!contractor) return <FileWall id={id} seat={seat} />;
  // Contractor field crews keep the phone as their surface; a brokerage field
  // seat is the listing agent (Run 10), and this door may be theirs.
  if (contractor.permissionLevel === 'field' && contractor.companyKind !== 'brokerage') {
    return <Navigate to="/mobile-required" replace />;
  }
  if ((contractor.accountRole === 'homeowner') !== (seat === 'homeowner')) {
    return <Navigate to={contractor.accountRole === 'homeowner' ? '/properties' : '/dashboard'} replace />;
  }
  return join ? <ParcelAdmission key={`${contractor.id}/${id}/${seat}`} id={id} seat={seat} token={null} />
    : <LoadDoor key={`${contractor.id}/${id}/${seat}/${support}`} id={id} seat={seat} support={support} token={null} />;
}

/** No token and no session: explain the contract instead of walling with sign-in. */
function FileWall({ id, seat }: { id: string; seat: SeatKind }) {
  return <div className="space-y-4">
    <Banner title="This URL is the seat">A LienGuardian parcel door opens with the seat link you were sent — that link carries its own credential. This address is missing its seat token, so there is no file to show.</Banner>
    <Card title={`${seatLabels[seat]} door`}>
      <p className="text-sm text-muted">Ask the party who invited you to resend your seat link for this parcel. Opening the bare address never grants access, and no login exists that substitutes for the link.</p>
      <p className="mt-3 text-sm text-muted">Already using LienGuardian for jobs or certified mail?{' '}
        <Link className="font-semibold text-tint" to={`/sign-in?returnTo=${encodeURIComponent(parcelDoor(id, seat))}`}>Sign in</Link>
        {' '}— optional, and not a way around the seat link.</p>
    </Card>
  </div>;
}

function LoadDoor({ id, seat, support, token }: { id: string; seat: SeatKind; support: boolean; token: string | null }) {
  const { contractor } = useSession();
  const file = useAsync(() => support ? parcelApi.viewAs(id, seat) : parcelApi.file(id, token), [id, seat, support, token]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  if (file.loading) return <Spinner label="Loading your parcel file…" />;
  if (file.error) return <div className="space-y-4"><Banner title="Parcel file unavailable">{file.error}. Access requires an admitted seat; title and counsel require seller consent.</Banner><Button variant="secondary" onClick={() => void file.reload()}>Reload file</Button></div>;
  const snapshot = file.data;
  if (!snapshot || (!token && !contractor)) return <Banner title="Parcel file unavailable" />;
  const target = snapshot.seats.find((s) => s.seat === seat && isAdmitted(s));
  const authorized = snapshot.parcel.id === id && snapshot.packet.parcelId === id && target &&
    (support ? snapshot.readOnly && snapshot.viewAs === seat
      // A capability response must confirm the door it was minted for; fail closed otherwise.
      : token ? snapshot.viewAs === null && snapshot.capability?.parcelId === id && snapshot.capability.seat === seat
      : snapshot.viewAs === null && target.contractorId === contractor!.id);
  if (!authorized) return <Banner title="This is not your parcel door">Use the URL for your admitted seat. Title and counsel require named seller consent.</Banner>;
  const access: ParcelAccess = { snapshot, seat, support, token,
    actorId: token ? target.contractorId ?? '' : contractor!.id,
    readOnlyAccount: !token && contractor?.permissionLevel === 'read_only' };
  const act = async (action: ParcelAction) => {
    if (busy) return;
    setBusy(true); setError(null); setMessage(null);
    try {
      await mutateParcel(access, action);
      setMessage('Saved. File refreshed from the parcel record.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The action could not be completed.');
    } finally {
      // Reconcile even after an ambiguous network failure; never retry a mutation automatically.
      await file.reload();
      setBusy(false);
    }
  };
  const exportPacket = async () => {
    if (busy) return;
    setBusy(true); setError(null);
    try {
      // Support export is the same server-authorized read-only view-as snapshot.
      const packet = support ? (await parcelApi.viewAs(id, seat)).packet : await parcelApi.export(id, token);
      if (packet.parcelId !== id) throw new Error('Export parcel does not match this door.');
      const url = URL.createObjectURL(new Blob([JSON.stringify(packet, null, 2)], { type: 'application/json' }));
      const link = document.createElement('a');
      link.href = url; link.download = `parcel-${id}.json`;
      document.body.append(link); link.click(); link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) { setError(err instanceof Error ? err.message : 'Export failed.'); }
    finally { setBusy(false); }
  };
  return <div className="space-y-4">
    {error && <Banner title="Action failed">{error}</Banner>}
    {message && !error && <Banner tone="ok">{message}</Banner>}
    <ParcelFile access={access} busy={busy} act={act} exportPacket={() => void exportPacket()} />
  </div>;
}
function ParcelAdmission({ id, seat, token }: { id: string; seat: SeatKind; token: string | null }) {
  const { contractor } = useSession();
  const [declared, setDeclared] = useState(false);
  const [busy, setBusy] = useState(false);
  const [admitted, setAdmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (admitted) return <Navigate to={parcelDoor(id, seat)} replace />;
  if (['homeowner', 'flipper', 'title', 'attorney'].includes(seat) || contractor?.permissionLevel === 'read_only') {
    return <Banner title="Admission unavailable">Seller consent is required for title and counsel. Read-only accounts cannot admit themselves.</Banner>;
  }
  return <Card title={`Accept ${seatLabels[seat]} invitation`}><form className="space-y-4" onSubmit={async (e) => {
    e.preventDefault(); if (busy || !declared) return;
    setBusy(true); setError(null);
    try { await parcelApi.admit(id, seat, token); setAdmitted(true); }
    catch (err) { setError(err instanceof Error ? err.message : 'Admission failed.'); }
    finally { setBusy(false); }
  }}>
    <p className="text-sm text-muted">{token
      ? 'This seat link carries the invitation addressed to your seat. Matching the folio alone does not grant membership.'
      : 'An invitation must already be addressed to your signed-in account. Matching the folio or opening this URL does not grant membership.'}</p>
    <label className="flex items-start gap-3 text-sm"><input type="checkbox" required checked={declared} onChange={(e) => setDeclared(e.target.checked)} />
      {seat === 'prime' ? 'I declare a direct contract with the owner (FL713_PRIVITY).' : seat === 'agent' ? 'I accept the listing-agent invitation addressed to my account.' : 'I declare furnishing under this construction relationship (FL713_FURNISHING).'}</label>
    {error && <Banner title="Admission failed">{error}</Banner>}
    <Button disabled={busy || !declared}>Accept addressed invitation</Button>
  </form></Card>;
}

/** Lookup opens only an existing, admitted seat belonging to this account. No customer chooser. */
export function ParcelLookup() {
  const { contractor } = useSession();
  const [county, setCounty] = useState('');
  const [folio, setFolio] = useState('');
  const [destination, setDestination] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (destination) return <Navigate to={destination} replace />;
  return <div className="max-w-xl space-y-5"><h1 className="text-xl font-bold">Open your parcel file</h1>
    <Card><form className="space-y-4" onSubmit={async (e) => {
      e.preventDefault(); setBusy(true); setError(null);
      try {
        const file = await parcelApi.lookup(county.trim(), folio.trim());
        const own = file.seats.filter((s) => s.contractorId === contractor?.id && isAdmitted(s));
        if (own.length !== 1 || file.viewAs) throw new Error('Use your assigned seat URL. Lookup requires one admitted customer seat.');
        setDestination(parcelDoor(file.parcel.id, own[0].seat));
      } catch (err) { setError(err instanceof Error ? err.message : 'Lookup failed.'); }
      finally { setBusy(false); }
    }}>
      <p className="text-sm text-muted">Use the county and folio for a file you already belong to, or open your assigned parcel link.</p>
      <Field label="County"><Input required maxLength={50} value={county} onChange={(e) => setCounty(e.target.value)} /></Field>
      <Field label="Folio"><Input required maxLength={100} value={folio} onChange={(e) => setFolio(e.target.value)} /></Field>
      {error && <Banner title="Could not open parcel">{error}</Banner>}
      <Button disabled={busy}>Open my parcel</Button>
    </form></Card>
  </div>;
}
